/**
 * PD-1523 — printable "Patient Demographic & Contact Information" sheet.
 *
 * Layout only: every value comes from `buildDemographicsSheet`. Sections,
 * headings and the checkbox/underline treatment mirror the PD-920 authorization
 * form (`OrderTests/pdf/generateAuthorizationFormPdf.ts`) so beHuman's printed
 * forms look like one family.
 *
 * Per Alina (PD-1523) the sheet shows only what beHuman actually holds: an empty
 * value drops its field, and a section left with no fields drops its heading too.
 */
import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { CanvasElement, Column, Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import type { Coverage, Patient } from '@medplum/fhirtypes'
import logo from '/logo.png'
import { urlToDataURL } from '@/pages/Patient/components/OrderTests/pdf/pdfHelpers'
import { BRAND_BLUE } from '@/pages/Patient/components/OrderTests/pdf/constants'
import { logError } from '@/utils/errorUtils'
import {
  buildDemographicsSheet,
  type DemographicsSheet,
  type SheetProviderInput,
  type SheetRelationship,
  type SheetSex,
} from '@/utils/demographicsSheet'

// ── pdfmake VFS bootstrap (same shape as the other generators) ───────────────
const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs
if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

type Margin = [number, number, number, number]

const PAGE_MARGIN = 40
// Room for the two-line disclaimer footer beneath the body.
const FOOTER_MARGIN = 60
// A4 is 595.28pt wide; what is left between the page margins.
const CONTENT_WIDTH = 595.28 - PAGE_MARGIN * 2
const COLUMN_GAP = 20
const LABEL_GRAY = '#555'

/** Width of one column when a row is split into `count` equal columns. */
function columnWidth(count: number): number {
  return (CONTENT_WIDTH - COLUMN_GAP * (count - 1)) / count
}

/** Bold section heading, e.g. "PATIENT INFORMATION". */
function heading(text: string): Content {
  return {
    text,
    fontSize: 9.5,
    bold: true,
    color: BRAND_BLUE,
    margin: [0, 9, 0, 4] as Margin,
  }
}

/** Label + value on an underline, keeping the look of the paper form. */
function field(label: string, value: string, width: number): Column {
  return {
    width,
    stack: [
      {
        text: [
          { text: `${label}: `, fontSize: 8.5, color: LABEL_GRAY },
          { text: value, fontSize: 9, bold: true },
        ],
      },
      {
        canvas: [
          { type: 'line', x1: 0, y1: 1, x2: width, y2: 1, lineWidth: 0.4, lineColor: '#bbb' },
        ],
      },
    ],
  }
}

/** One row of equally-sized fields. */
function fieldRow(fields: { label: string; value: string }[]): Content {
  const width = columnWidth(fields.length)
  return {
    columns: fields.map((f) => field(f.label, f.value, width)),
    columnGap: COLUMN_GAP,
    margin: [0, 1, 0, 6] as Margin,
  }
}

/**
 * Canvas-drawn checkbox + label. Canvas rather than a "☐" glyph because
 * pdfmake's bundled Roboto has no box characters (same reason as PD-920).
 */
function checkbox(checked: boolean, label: string, margin: Margin = [0, 0, 0, 0]): Column {
  const size = 7
  const canvas: CanvasElement[] = [
    { type: 'rect', x: 0, y: 2, w: size, h: size, lineWidth: 0.5, lineColor: '#000' },
  ]
  if (checked) {
    canvas.push(
      {
        type: 'line',
        x1: 1,
        y1: 3.5,
        x2: size - 1,
        y2: size + 1.5,
        lineWidth: 0.8,
        lineColor: '#000',
      },
      {
        type: 'line',
        x1: size - 1,
        y1: 3.5,
        x2: 1,
        y2: size + 1.5,
        lineWidth: 0.8,
        lineColor: '#000',
      },
    )
  }
  return {
    width: 'auto',
    columns: [
      { canvas, width: size + 4 },
      // noWrap: an auto-width column otherwise breaks a short label onto a
      // second line when pdfmake tightens the row.
      { text: label, fontSize: 9, bold: checked, width: 'auto', noWrap: true },
    ],
    columnGap: 0,
    margin,
  }
}

/**
 * A row of checkboxes after a label. The "Other" box appears only when that is
 * what was recorded, and carries its label ("Other: Injured Party") — the boxes
 * we can neither tick nor caption are left off entirely.
 */
function checkboxRow(
  label: string,
  options: { value: string; text: string }[],
  selected: string,
  otherText: string,
): Content {
  const boxes = options.map((option) =>
    checkbox(option.value === selected, option.text, [0, 0, 12, 0]),
  )
  if (selected === 'other') {
    boxes.push(checkbox(true, otherText ? `Other: ${otherText}` : 'Other', [0, 0, 12, 0]))
  }
  const columns: Column[] = [
    {
      text: `${label}:`,
      fontSize: 8.5,
      color: LABEL_GRAY,
      width: 'auto',
      noWrap: true,
      margin: [0, 1, 6, 0] as Margin,
    },
    ...boxes,
  ]
  return { columns, columnGap: 0, margin: [0, 1, 0, 7] as Margin }
}

const SEX_OPTIONS: { value: SheetSex; text: string }[] = [
  { value: 'male', text: 'Male' },
  { value: 'female', text: 'Female' },
]

const RELATIONSHIP_OPTIONS: { value: SheetRelationship; text: string }[] = [
  { value: 'self', text: 'Self' },
  { value: 'spouse', text: 'Spouse' },
  { value: 'parent', text: 'Parent' },
  { value: 'child', text: 'Child' },
]

function letterhead(logoDataUrl: string, generatedOn: string): Content {
  return {
    columns: [
      logoDataUrl
        ? { image: logoDataUrl, width: 55, margin: [0, 0, 18, 0] as Margin }
        : { text: '', width: 55 },
      {
        stack: [
          { text: 'BeHuman Health', fontSize: 9, bold: true, color: BRAND_BLUE },
          {
            text: '2500 E Hallandale Beach Blvd, Suite 400, Hallandale Beach, FL, 33009',
            fontSize: 7,
            color: LABEL_GRAY,
            margin: [0, 2, 0, 0] as Margin,
          },
          { text: 'Fax: (561) 821-9872', fontSize: 7, color: LABEL_GRAY, margin: [0, 1, 0, 0] },
        ],
      },
      {
        text: `Generated ${generatedOn}`,
        fontSize: 7,
        color: LABEL_GRAY,
        alignment: 'right',
        width: 'auto',
      },
    ],
    columnGap: 8,
    margin: [0, 0, 0, 6] as Margin,
  }
}

/**
 * The PHI disclaimer, pinned to the foot of every page rather than trailing the
 * last section — it qualifies the whole sheet, not the content above it.
 * Exported for tests; pdfmake calls it once per page.
 */
export function phiFooter(): Content {
  return {
    text: 'This demographic sheet contains protected health information (PHI) and should be transmitted and handled in accordance with applicable privacy and security requirements.',
    fontSize: 7.5,
    italics: true,
    color: LABEL_GRAY,
    margin: [PAGE_MARGIN, 12, PAGE_MARGIN, 0] as Margin,
  }
}

/** A row is either labelled values or one line of checkboxes. */
type SheetRow =
  | { fields: { label: string; value: string }[] }
  | {
      choice: {
        label: string
        options: { value: string; text: string }[]
        selected: string
        /** Caption for a value outside the listed boxes; only used for 'other'. */
        otherText?: string
      }
    }

type SheetSection = { heading: string; rows: SheetRow[] }

function sectionsOf(sheet: DemographicsSheet): SheetSection[] {
  return [
    {
      heading: 'PATIENT INFORMATION',
      rows: [
        {
          fields: [
            { label: 'Patient Full Name', value: sheet.patient.fullName },
            { label: 'Date of Birth', value: sheet.patient.dateOfBirth },
          ],
        },
        { choice: { label: 'Sex', options: SEX_OPTIONS, selected: sheet.patient.sex } },
        {
          fields: [
            { label: 'Preferred Name', value: sheet.patient.preferredName },
            { label: 'Preferred Language', value: sheet.patient.preferredLanguage },
          ],
        },
      ],
    },
    {
      heading: 'CONTACT INFORMATION',
      rows: [
        {
          fields: [
            { label: 'Primary Phone', value: sheet.contact.primaryPhone },
            { label: 'Alternate Phone', value: sheet.contact.alternatePhone },
          ],
        },
        { fields: [{ label: 'Email Address', value: sheet.contact.email }] },
      ],
    },
    {
      heading: 'MAILING ADDRESS',
      rows: [
        { fields: [{ label: 'Street Address', value: sheet.address.street }] },
        {
          fields: [
            { label: 'City', value: sheet.address.city },
            { label: 'State', value: sheet.address.state },
            { label: 'ZIP Code', value: sheet.address.zip },
          ],
        },
      ],
    },
    {
      heading: 'INSURANCE INFORMATION',
      rows: [
        {
          fields: [
            { label: 'Primary Insurance', value: sheet.insurance.primaryInsurance },
            { label: 'Member ID', value: sheet.insurance.memberId },
          ],
        },
        {
          fields: [
            { label: 'Group Number', value: sheet.insurance.groupNumber },
            { label: 'Subscriber Name', value: sheet.insurance.subscriberName },
          ],
        },
        {
          fields: [
            { label: 'Subscriber Date of Birth', value: sheet.insurance.subscriberDateOfBirth },
          ],
        },
        {
          choice: {
            label: 'Relationship to Subscriber',
            options: RELATIONSHIP_OPTIONS,
            selected: sheet.insurance.relationship,
            otherText: sheet.insurance.relationshipOther,
          },
        },
      ],
    },
    {
      heading: 'EMERGENCY CONTACT',
      rows: [
        {
          fields: [
            { label: 'Name', value: sheet.emergencyContact.name },
            { label: 'Relationship', value: sheet.emergencyContact.relationship },
            { label: 'Phone Number', value: sheet.emergencyContact.phone },
          ],
        },
      ],
    },
    {
      heading: 'PHARMACY INFORMATION',
      rows: [
        {
          fields: [
            { label: 'Preferred Pharmacy', value: sheet.pharmacy.name },
            { label: 'Pharmacy Phone', value: sheet.pharmacy.phone },
          ],
        },
        { fields: [{ label: 'Pharmacy Address', value: sheet.pharmacy.address }] },
      ],
    },
    {
      heading: 'PROVIDER INFORMATION',
      rows: [
        {
          fields: [
            { label: 'Provider Name', value: sheet.provider.providerName },
            { label: 'NPI', value: sheet.provider.npi },
          ],
        },
        {
          fields: [
            { label: 'Phone', value: sheet.provider.phone },
            { label: 'Fax', value: sheet.provider.fax },
          ],
        },
      ],
    },
  ]
}

/**
 * Renders only what beHuman holds (Alina, PD-1523): an empty value drops its
 * field, a row with nothing left drops out, and a section whose rows all
 * dropped takes its heading with it.
 */
function renderSections(sections: SheetSection[]): Content[] {
  const content: Content[] = []
  for (const section of sections) {
    const rows: Content[] = []
    for (const row of section.rows) {
      if ('choice' in row) {
        if (!row.choice.selected) continue
        rows.push(
          checkboxRow(
            row.choice.label,
            row.choice.options,
            row.choice.selected,
            row.choice.otherText ?? '',
          ),
        )
      } else {
        const populated = row.fields.filter((f) => f.value)
        if (populated.length) rows.push(fieldRow(populated))
      }
    }
    if (rows.length) content.push(heading(section.heading), ...rows)
  }
  return content
}

/** Builds the pdfmake document for a demographics sheet. Exported for tests. */
export function buildDemographicsDocDefinition(
  sheet: DemographicsSheet,
  logoDataUrl: string,
  generatedOn: string,
): TDocumentDefinitions {
  const content: Content[] = [
    letterhead(logoDataUrl, generatedOn),
    {
      text: 'PATIENT DEMOGRAPHIC & CONTACT INFORMATION',
      fontSize: 12,
      bold: true,
      alignment: 'center',
      margin: [0, 6, 0, 4] as Margin,
    },
    ...renderSections(sectionsOf(sheet)),
  ]

  return {
    pageSize: 'A4',
    // Deeper bottom margin than the rest: the disclaimer footer is drawn inside it.
    pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, FOOTER_MARGIN],
    content,
    footer: phiFooter,
    defaultStyle: { fontSize: 9 },
  }
}

/** `Demographics_Jane_Doe_2026-08-26.pdf`, or a patient-less fallback. */
function fileNameFor(fullName: string): string {
  const name = fullName
    .trim()
    .replace(/[^\w]+/g, '_')
    .replace(/^_|_$/g, '')
  const today = new Date().toISOString().split('T')[0]
  return `Demographics_${name || 'Patient'}_${today}.pdf`
}

/**
 * Renders the sheet and hands it to the browser: a new tab (where the user hits
 * print), falling back to a download when a popup blocker refuses the tab.
 */
export async function printDemographicsSheet(
  patient: Patient,
  primaryCoverage: Coverage | undefined,
  provider: SheetProviderInput | undefined,
): Promise<void> {
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    // A missing logo must not cost the user their printout.
    logError('patient', 'BeHuman logo fetch for demographics sheet', err)
  }

  const sheet = buildDemographicsSheet(patient, primaryCoverage, provider)
  const generatedOn = new Date().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
  const fileName = fileNameFor(sheet.patient.fullName)

  pdfMake
    .createPdf(buildDemographicsDocDefinition(sheet, logoDataUrl, generatedOn))
    .getBlob((blob) => {
      const url = URL.createObjectURL(blob)
      const tab = window.open(url, '_blank')
      if (!tab) {
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    })
}
